import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetActiveBankAccounts,
  GetBankAccounts,
  GetBankAccountsWithDeleted,
  CreateBankAccount,
  UpdateBankAccount,
  DeleteBankAccount,
  GetBankTransactions,
  CreateBankTransaction } from "../Api";
import { 
  ServiceQueryOptions,
  unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME, LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const bankQueryKeys = {
  activeBankAccounts: () => ["activeBankAccounts"] as const,
  bankAccounts: (params?: any) => ["bankAccounts", params] as const,
  bankAccountsWithDeleted: (params?: any) => ["bankAccountsWithDeleted", params] as const,
  bankTransactions: (bankAccountId: string, params?: any) =>
    ["bankTransactions", bankAccountId, params] as const,
};

export const invalidateBankQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["activeBankAccounts"] });
  queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
  queryClient.invalidateQueries({ queryKey: ["bankAccountsWithDeleted"] });
  queryClient.invalidateQueries({ queryKey: ["bankTransactions"] });
};

export const useActiveBankAccountsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: bankQueryKeys.activeBankAccounts(),
    queryFn: async () => unwrapApiResponse(await GetActiveBankAccounts()),
  });
};

export const useBankAccountsQuery = (
  params?: { page?: number; limit?: number; search?: string },
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: bankQueryKeys.bankAccounts(params),
    queryFn: async () => unwrapApiResponse(await GetBankAccounts(params)),
  });
};

export const useBankAccountsWithDeletedQuery = (
  params?: { page?: number; limit?: number; search?: string },
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: bankQueryKeys.bankAccountsWithDeleted(params),
    queryFn: async () => unwrapApiResponse(await GetBankAccountsWithDeleted(params)),
  });
};

export const useCreateBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => CreateBankAccount(data),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateBankQueries(queryClient);
      }
    },
  });
};

export const useUpdateBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => UpdateBankAccount(data),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateBankQueries(queryClient);
      }
    },
  });
};

export const useDeleteBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => DeleteBankAccount(id),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateBankQueries(queryClient);
      }
    },
  });
};

export const useBankTransactionsQuery = (
  bank_account_id: string,
  params?: { page?: number; limit?: number },
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!bank_account_id,
    ...options,
    queryKey: bankQueryKeys.bankTransactions(bank_account_id, params),
    queryFn: async () =>
      unwrapApiResponse(await GetBankTransactions(bank_account_id, params)),
  });
};

export const useCreateBankTransactionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => CreateBankTransaction(data),
    onSuccess: (res, variables) => {
      if (res?.success) {
        invalidateBankQueries(queryClient);
        if (variables.bank_account_id) {
          queryClient.invalidateQueries({
            queryKey: ["bankTransactions", variables.bank_account_id],
          });
        }
      }
    },
  });
};

export const useBankService = (config?: {
  fetchActiveBankAccountsEnabled?: boolean;
  fetchBankAccountsEnabled?: boolean;
  fetchBankAccountsParams?: { page?: number; limit?: number; search?: string };
  fetchBankAccountsWithDeletedEnabled?: boolean;
  fetchBankAccountsWithDeletedParams?: { page?: number; limit?: number; search?: string };
}) => {
  const queryClient = useQueryClient();

  const activeBankAccountsQuery = useActiveBankAccountsQuery({
    enabled: config?.fetchActiveBankAccountsEnabled !== false,
  });

  const bankAccountsQuery = useBankAccountsQuery(
    config?.fetchBankAccountsParams,
    {
      enabled: !!config?.fetchBankAccountsEnabled,
    },
  );

  const bankAccountsWithDeletedQuery = useBankAccountsWithDeletedQuery(
    config?.fetchBankAccountsWithDeletedParams,
    {
      enabled: !!config?.fetchBankAccountsWithDeletedEnabled,
    },
  );

  const createBankAccountMutation = useCreateBankAccountMutation();
  const updateBankAccountMutation = useUpdateBankAccountMutation();
  const deleteBankAccountMutation = useDeleteBankAccountMutation();
  const createBankTransactionMutation = useCreateBankTransactionMutation();

  return {
    activeBankAccountsQuery,
    bankAccountsQuery,
    bankAccountsWithDeletedQuery,
    createBankAccountMutation,
    updateBankAccountMutation,
    deleteBankAccountMutation,
    createBankTransactionMutation,
  };
};
